/**
 * Simple Browser-Native Mumble Audio Engine
 * No Node.js dependencies - pure Web APIs
 */

import Recorder from 'opus-recorder';
import AudioPlayer from './audio-player.js';

export class SimpleMumbleAudio {
  constructor() {
    this.ws = null;
    this.recorder = null;
    this.connected = false;
    this.recording = false;
    this.sessionId = 0;
    this.voiceSequence = 0;
    this.oggBuffer = new Uint8Array(0);
    this.skippedHeaders = 0; // Track if we've skipped OpusHead and OpusTags
    this.pingInterval = null;
    this.pingTimestamp = 0;
    this.users = new Map(); // Map of session ID -> user info
    this.onUsersChanged = null; // Callback for when users change

    // Audio playback
    this.audioContext = null;
    this.audioPlayers = new Map(); // session → AudioPlayer instance
    this.masterOutputGain = null;
    this._audioContextResumeInProgress = false; // Prevent resume() spam

    // Separate AudioContext for mic capture — keeps playback context out of
    // Chrome Android "communication" mode which routes audio to earpiece
    this.micAudioContext = null;

    // Microphone stream (acquired once, reused for each PTT press)
    this.micStream = null;

    // Channel tracking
    this.channels = new Map();
    this.onChannelsChanged = null;

    // iOS keep-alive oscillator
    this.keepAliveOscillator = null;
    this.keepAliveGain = null;

    // Callbacks
    this.onDisconnected = null;
    this.onServerSync = null;

    // Pong timeout tracking
    this._lastPongTime = 0;

    // Transmission state management (prevents race conditions on iOS)
    this._transmissionStarting = false;
    this._pendingStop = false;

    // Background state: skip audio playback while suspended (notifications still work)
    this._suspended = false;

    // Background resume: discard stale audio for a short window after foregrounding
    this._discardAudioUntil = 0;

    // Drain timer for PTT release (allows in-flight frames to flush)
    this._drainTimer = null;
    this._drainStart = 0;
    this._lastOggPageTime = 0;
    this._framesSentActive = 0;  // frames sent while recording===true
    this._framesSentDrain = 0;   // frames sent while recording==='draining'

    // Connection quality tracking
    this._rttHistory = [];        // Last 10 RTT samples
    this._jitter = 0;             // Current jitter (ms)
    this._avgRtt = 0;             // Current avg RTT (ms)
    this._connectionQuality = 'good';  // 'good' | 'warning' | 'poor'
    this._qualityCallback = null;

    // Adaptive encoding — bitrate adjusts to network conditions
    this._targetBitRate = 40000;       // Current target bitrate
    this._bitrateByQuality = {
      good: 40000,     // Full quality
      warning: 24000,  // Reduced quality, saves bandwidth
      poor: 16000      // Minimum viable voice quality
    };
  }

  // Parse Ogg page to extract Opus frames
  _parseOggPage(data) {
    const frames = [];
    let offset = 0;

    while (offset < data.length) {
      // Check for Ogg page header "OggS"
      if (offset + 27 > data.length) break;

      if (data[offset] !== 0x4F || data[offset + 1] !== 0x67 ||
          data[offset + 2] !== 0x67 || data[offset + 3] !== 0x53) {
        // Not an Ogg page, might be partial data
        break;
      }

      // Skip to page segments count (byte 26)
      const numSegments = data[offset + 26];
      const segmentTableOffset = offset + 27;

      if (segmentTableOffset + numSegments > data.length) break;

      // Read segment table to calculate total payload size
      const segmentTable = Array.from(data.slice(segmentTableOffset, segmentTableOffset + numSegments));
      const totalPayloadSize = segmentTable.reduce((sum, size) => sum + size, 0);

      // Start of actual data
      const dataStart = segmentTableOffset + numSegments;

      if (dataStart + totalPayloadSize > data.length) break;

      // Extract frames from segment table
      // In Ogg, segments of 255 bytes continue the same frame
      // A segment < 255 marks the end of a frame
      let currentFrameStart = dataStart;
      let currentFrameSize = 0;

      for (let i = 0; i < numSegments; i++) {
        const segmentSize = segmentTable[i];
        currentFrameSize += segmentSize;

        // Frame ends when segment is < 255 or at last segment
        if (segmentSize < 255 || i === numSegments - 1) {
          const frameData = data.slice(currentFrameStart, currentFrameStart + currentFrameSize);

          // Skip Ogg header packets (OpusHead, OpusTags)
          if (frameData.length > 0 && frameData[0] !== 0x4F) {
            frames.push(frameData);
          }

          currentFrameStart += currentFrameSize;
          currentFrameSize = 0;
        }
      }

      // Move to next page
      offset = dataStart + totalPayloadSize;
    }

    return frames;
  }

  // Encode variable-length integer (varint)
  _encodeVarint(value) {
    const bytes = [];
    if (value < 0) {
      value = ~value;
      if (value <= 0x3) {
        bytes.push(0xFC | value);
        return new Uint8Array(bytes);
      }
    }

    if (value < 0x80) {
      bytes.push(value);
    } else if (value < 0x4000) {
      bytes.push(0x80 | (value >> 8));
      bytes.push(value & 0xFF);
    } else if (value < 0x200000) {
      bytes.push(0xC0 | (value >> 16));
      bytes.push((value >> 8) & 0xFF);
      bytes.push(value & 0xFF);
    } else if (value < 0x10000000) {
      bytes.push(0xE0 | (value >> 24));
      bytes.push((value >> 16) & 0xFF);
      bytes.push((value >> 8) & 0xFF);
      bytes.push(value & 0xFF);
    } else {
      bytes.push(0xF0);
      bytes.push((value >> 24) & 0xFF);
      bytes.push((value >> 16) & 0xFF);
      bytes.push((value >> 8) & 0xFF);
      bytes.push(value & 0xFF);
    }

    return new Uint8Array(bytes);
  }

  // Decode variable-length integer (varint)
  _decodeVarint(data, offset = 0) {
    const first = data[offset];

    if ((first & 0x80) === 0x00) {
      return { value: first & 0x7F, length: 1 };
    } else if ((first & 0xC0) === 0x80) {
      return {
        value: ((first & 0x3F) << 8) | data[offset + 1],
        length: 2
      };
    } else if ((first & 0xF0) === 0xF0) {
      if ((first & 0xFC) === 0xF0) {
        return {
          value: (data[offset + 1] << 24) | (data[offset + 2] << 16) |
                 (data[offset + 3] << 8) | data[offset + 4],
          length: 5
        };
      } else if ((first & 0xFC) === 0xFC) {
        return { value: ~(first & 0x03), length: 1 };
      }
    } else if ((first & 0xF0) === 0xE0) {
      return {
        value: ((first & 0x0F) << 24) | (data[offset + 1] << 16) |
               (data[offset + 2] << 8) | data[offset + 3],
        length: 4
      };
    } else if ((first & 0xE0) === 0xC0) {
      return {
        value: ((first & 0x1F) << 16) | (data[offset + 1] << 8) | data[offset + 2],
        length: 3
      };
    }

    return { value: 0, length: 1 };
  }

  // Encode standard protobuf varint (different from Mumble's custom varint!)
  _encodeProtobufVarintRaw(value) {
    const bytes = [];
    // Handle numbers larger than 32-bit safely
    if (value >= 0x100000000) {
      // For 64-bit numbers, use BigInt
      let bigValue = BigInt(value);
      while (bigValue > 0x7Fn) {
        bytes.push(Number(bigValue & 0x7Fn) | 0x80);
        bigValue >>= 7n;
      }
      bytes.push(Number(bigValue & 0x7Fn));
    } else {
      // For 32-bit numbers, use regular arithmetic
      while (value > 0x7F) {
        bytes.push((value & 0x7F) | 0x80);
        value >>>= 7; // Unsigned right shift
      }
      bytes.push(value & 0x7F);
    }
    return new Uint8Array(bytes);
  }

  // Handle incoming voice packet from another user
  _handleVoicePacket(data) {
    if (data.length < 2) return;

    try {
      // Parse voice packet header
      const type = data[0];
      const target = type & 0x1F; // Lower 5 bits
      const codec = (type >> 5) & 0x07; // Upper 3 bits (0=CELT Alpha, 4=Opus)

      // Decode session ID (varint)
      const sessionResult = this._decodeVarint(data, 1);
      const session = sessionResult.value;
      let offset = 1 + sessionResult.length;

      // Decode sequence number (varint) - we don't use it but must skip it
      const sequenceResult = this._decodeVarint(data, offset);
      offset += sequenceResult.length;

      const dataStart = offset;

      // Mark user as speaking
      const user = this.users.get(session);
      if (user) {
        // Only log and update if state is changing
        const wasAlreadySpeaking = user.speaking;
        user.speaking = true;

        if (!wasAlreadySpeaking) {
          console.log('[SimpleMumble]', user.name || `Session ${session}`, 'is speaking');
          // Notify UI only when speaking state changes
          if (this.onUsersChanged) {
            this.onUsersChanged(Array.from(this.users.values()));
          }
        }

        // Clear speaking flag after a delay
        clearTimeout(user.speakingTimeout);
        user.speakingTimeout = setTimeout(() => {
          user.speaking = false;
          if (this.onUsersChanged) {
            this.onUsersChanged(Array.from(this.users.values()));
          }
          console.log('[SimpleMumble]', user.name || `Session ${session}`, 'stopped speaking');
        }, 1500); // Clear after 1.5 seconds of silence
      }

      // Decode and play audio if Opus codec (4)
      // Skip while suspended (background) or during post-resume discard window
      if (codec === 4 && this.audioContext && !this._suspended && Date.now() >= this._discardAudioUntil) {
        // Try to resume AudioContext if suspended (don't block, don't drop audio)
        if (this.audioContext.state === 'suspended' && !this._audioContextResumeInProgress) {
          this._audioContextResumeInProgress = true;
          this.audioContext.resume().then(() => {
            console.log('[SimpleMumble] AudioContext resumed on voice packet - state:', this.audioContext.state);
            this._audioContextResumeInProgress = false;
          }).catch(() => {
            this._audioContextResumeInProgress = false;
          });
        }

        // Get or create audio player for this user (synchronous — no race condition)
        let player = this.audioPlayers.get(session);
        if (!player) {
          player = new AudioPlayer(session, this.audioContext, this.masterOutputGain);
          player.setConnectionQuality(this._connectionQuality);
          this.audioPlayers.set(session, player);
        }

        // Extract ALL Opus frames from the packet (there can be multiple!)
        let offset = dataStart;
        while (offset < data.length) {
          // Decode length varint
          const lengthResult = this._decodeVarint(data, offset);
          const length = lengthResult.value & ~0x2000; // Clear termination flag
          const isTerminator = (lengthResult.value & 0x2000) !== 0;

          offset += lengthResult.length;

          // Extract Opus frame
          if (length > 0 && offset + length <= data.length) {
            const opusFrame = data.slice(offset, offset + length);
            offset += length;

            // Decode and play this frame (synchronous for performance)
            player.decodeAndPlay(opusFrame);
          }

          // If this was the last frame (terminator bit set), stop
          if (isTerminator) {
            break;
          }
        }
      }

    } catch (e) {
      console.error('[SimpleMumble] Error handling voice packet:', e);
    }
  }

  // Parse UserState protobuf message
  _parseUserState(payload) {
    try {
      const userInfo = {};
      let offset = 0;

      while (offset < payload.length) {
        if (offset >= payload.length) break;

        // Read field tag as varint (handles multi-byte tags for field numbers >= 16)
        const tagResult = this._decodeProtobufVarint(payload, offset);
        offset += tagResult.length;
        const fieldNum = tagResult.value >> 3;
        const wireType = tagResult.value & 0x7;

        if (fieldNum === 1 && wireType === 0) {
          // session (uint32)
          const result = this._decodeProtobufVarint(payload, offset);
          userInfo.session = result.value;
          offset += result.length;
        } else if (fieldNum === 3 && wireType === 2) {
          // name (string) - field 3, not field 2!
          const result = this._decodeProtobufString(payload, offset);
          userInfo.name = result.value;
          offset += result.length;
        } else if (fieldNum === 4 && wireType === 0) {
          // user_id (uint32)
          const result = this._decodeProtobufVarint(payload, offset);
          userInfo.userId = result.value;
          offset += result.length;
        } else if (fieldNum === 5 && wireType === 0) {
          // channel_id (uint32)
          const result = this._decodeProtobufVarint(payload, offset);
          userInfo.channelId = result.value;
          offset += result.length;
        } else {
          // Skip unknown field
          if (wireType === 0) {
            // Varint
            const result = this._decodeProtobufVarint(payload, offset);
            offset += result.length;
          } else if (wireType === 2) {
            // Length-delimited
            const lengthResult = this._decodeProtobufVarint(payload, offset);
            offset += lengthResult.length + lengthResult.value;
          } else if (wireType === 5) {
            // Fixed 32-bit
            offset += 4;
          } else if (wireType === 1) {
            // Fixed 64-bit
            offset += 8;
          } else {
            // Unknown wire type, stop parsing
            break;
          }
        }
      }

      // Store or update user
      if (userInfo.session !== undefined) {
        const existingUser = this.users.get(userInfo.session);
        const updatedUser = { ...existingUser, ...userInfo };

        // Default channelId to 0 (Root) — server omits field when value is 0 (proto3 default)
        if (updatedUser.channelId === undefined) {
          updatedUser.channelId = 0;
        }

        // Add isSelf flag
        updatedUser.isSelf = userInfo.session === this.sessionId;

        // Use session ID as fallback name if no name provided
        if (!updatedUser.name) {
          updatedUser.name = `User ${updatedUser.session}`;
        }

        this.users.set(userInfo.session, updatedUser);
        console.log('[SimpleMumble] User update:', updatedUser.name, `session=${updatedUser.session} ch=${updatedUser.channelId} isSelf=${updatedUser.isSelf}`);

        // Notify UI
        if (this.onUsersChanged) {
          this.onUsersChanged(Array.from(this.users.values()));
        }
      }
    } catch (e) {
      console.error('[SimpleMumble] Error parsing UserState:', e);
    }
  }

  // Decode standard protobuf varint (different from Mumble's custom varint)
  _decodeProtobufVarint(data, offset) {
    let value = 0;
    let shift = 0;
    let length = 0;

    while (offset + length < data.length) {
      const byte = data[offset + length];
      value |= (byte & 0x7F) << shift;
      length++;
      shift += 7;

      if ((byte & 0x80) === 0) {
        break;
      }

      if (length > 10) {
        throw new Error('Varint too long');
      }
    }

    return { value, length };
  }

  // Decode protobuf string (length-delimited)
  _decodeProtobufString(data, offset) {
    const lengthResult = this._decodeProtobufVarint(data, offset);
    const stringStart = offset + lengthResult.length;
    const stringEnd = stringStart + lengthResult.value;

    if (stringEnd > data.length) {
      throw new Error('String extends beyond buffer');
    }

    const stringBytes = data.slice(stringStart, stringEnd);
    const value = new TextDecoder().decode(stringBytes);

    return {
      value,
      length: lengthResult.length + lengthResult.value
    };
  }

  // Create Mumble voice packet (Opus format)
  // IMPORTANT: Outgoing packets do NOT include session ID (server already knows who we are)
  _createVoicePacket(opusData, isTerminator = false) {
    // Voice packet type: Opus (4) with normal speech target (0)
    const type = (4 << 5) | 0;

    // Encode sequence number as varint
    const sequenceVarint = this._encodeVarint(this.voiceSequence++);

    // For Opus: encode length with termination bit
    // Length varint with bit 13 set for termination
    const length = isTerminator ? (opusData.length | 0x2000) : opusData.length;
    const lengthVarint = this._encodeVarint(length);

    // Build final packet: type + sequence + length + opus data
    // NOTE: NO session ID for outgoing packets!
    const packet = new Uint8Array(
      1 + sequenceVarint.length + lengthVarint.length + opusData.length
    );

    let offset = 0;
    packet[offset++] = type;
    packet.set(sequenceVarint, offset);
    offset += sequenceVarint.length;
    packet.set(lengthVarint, offset);
    offset += lengthVarint.length;
    packet.set(opusData, offset);

    return packet;
  }

  async connect(options = {}) {
    const {
      address = window.location.hostname,
      username = 'User',
      wsUrl: customWsUrl = null,
      onConnected = () => {},
      onError = (err) => console.error(err),
      onUsersChanged = null,
      onChannelsChanged = null,
      onDisconnected = null,
      onServerSync = null,
      onQualityChanged = null
    } = options;

    this.username = username;
    this.onUsersChanged = onUsersChanged;
    this.onChannelsChanged = onChannelsChanged;
    this.onDisconnected = onDisconnected;
    this.onServerSync = onServerSync;
    this._qualityCallback = onQualityChanged;

    // Reset quality tracking on new connection
    this._rttHistory = [];
    this._connectionQuality = 'good';

    try {
      console.log('[SimpleMumble] Connecting to', address);

      // Build WebSocket URL - use port 8443 where Caddy handles WebSocket proxy
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const port = window.location.protocol === 'https:' ? '8443' : '8080';
      const wsUrl = customWsUrl || `${protocol}//${address}:${port}/mumble-ws`;
      console.log('[SimpleMumble] WebSocket URL:', wsUrl);

      // Create WebSocket with 'binary' subprotocol required by mumble-web-proxy
      this.ws = new WebSocket(wsUrl, ['binary']);
      this.ws.binaryType = 'arraybuffer';

      // Wait for connection
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('WebSocket timeout')), 10000);

        this.ws.onopen = () => {
          clearTimeout(timeout);
          console.log('[SimpleMumble] WebSocket connected');
          this.connected = true;
          resolve();
        };

        this.ws.onerror = (error) => {
          clearTimeout(timeout);
          console.error('[SimpleMumble] WebSocket error:', error);
          reject(error);
        };
      });

      // Setup message handling
      this.ws.onmessage = (event) => {
        this._handleMessage(event.data);
      };

      this.ws.onclose = (event) => {
        console.log('[SimpleMumble] WebSocket closed - Code:', event.code, 'Reason:', event.reason, 'Clean:', event.wasClean);
        this.connected = false;
        this._stopPingInterval();
        if (this.onDisconnected) {
          this.onDisconnected({ code: event.code, reason: event.reason, wasClean: event.wasClean, kickedByServer: !!this._kickedByServer });
        }
        this._kickedByServer = false;
      };

      // Initialize audio playback first (creates AudioContext needed by recorder)
      await this._initAudioPlayback();

      // Initialize recorder in background — on iOS after OAuth redirect,
      // getUserMedia may silently hang without user gesture context.
      // Connection works for listening; mic is acquired on first PTT press if deferred.
      this._initRecorder().catch(err => {
        console.warn('[SimpleMumble] Recorder init deferred:', err.message);
        this._micDeferred = true;
      });

      // Catch-all: resume AudioContext on ANY user touch/click
      // iPad Safari can suspend AudioContext at unexpected times
      this._userGestureHandler = () => {
        if (this.audioContext && this.audioContext.state === 'suspended') {
          console.log('[SimpleMumble] Resuming AudioContext on user gesture');
          this.audioContext.resume();
        }
      };
      document.addEventListener('touchstart', this._userGestureHandler, { passive: true });
      document.addEventListener('click', this._userGestureHandler, { passive: true });

      // Listen for device changes (Bluetooth connect/disconnect, headset plug)
      this._deviceChangeHandler = () => this._handleDeviceChange();
      navigator.mediaDevices.addEventListener('devicechange', this._deviceChangeHandler);

      // Start sending periodic Pings to keep connection alive
      this._startPingInterval();

      console.log('[SimpleMumble] Fully connected! AudioContext state:', this.audioContext.state);
      onConnected();

    } catch (error) {
      console.error('[SimpleMumble] Connection failed:', error);
      onError(error);
      throw error;
    }
  }

  _startPingInterval() {
    // Send Ping every 5 seconds to keep connection alive (more frequent than default)
    console.log('[SimpleMumble] Starting ping interval (every 5 seconds)');
    this._lastPongTime = Date.now();
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // If no pong in 8s, connection is dead (fast detection for network switches)
        if (Date.now() - this._lastPongTime > 8000) {
          console.error('[SimpleMumble] Pong timeout - closing connection');
          this.ws.close(4000, 'Pong timeout');
          return;
        }

        // Create Ping protobuf message with timestamp field
        // Ping message has optional uint64 timestamp = 1;
        this.pingTimestamp = Date.now();

        // Encode as protobuf: field 1 (timestamp) = tag 0x08 + PROTOBUF varint value
        // IMPORTANT: Use protobuf varint, not Mumble's custom varint!
        const timestampVarint = this._encodeProtobufVarintRaw(this.pingTimestamp);
        const payload = new Uint8Array(1 + timestampVarint.length);
        payload[0] = 0x08; // Field 1, wire type 0 (varint)
        payload.set(timestampVarint, 1);

        // Silently send ping (no logging to reduce console spam)
        this._sendControlMessage(3, payload); // Type 3 = Ping
      }
    }, 5000); // Every 5 seconds (standard Mumble interval)
  }

  _stopPingInterval() {
    if (this.pingInterval) {
      console.log('[SimpleMumble] Stopping ping interval');
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  _updateQuality() {
    if (this._rttHistory.length < 2) return;
    // Average RTT
    this._avgRtt = this._rttHistory.reduce((a, b) => a + b, 0) / this._rttHistory.length;
    // Jitter: average difference between consecutive RTTs
    let jitterSum = 0;
    for (let i = 1; i < this._rttHistory.length; i++) {
      jitterSum += Math.abs(this._rttHistory[i] - this._rttHistory[i - 1]);
    }
    this._jitter = jitterSum / (this._rttHistory.length - 1);
    // Quality thresholds (tuned for PTT over cellular/Starlink)
    let quality;
    if (this._avgRtt < 300 && this._jitter < 80) {
      quality = 'good';       // Green — normal operation
    } else if (this._avgRtt < 600 && this._jitter < 150) {
      quality = 'warning';    // Yellow — degraded but usable
    } else {
      quality = 'poor';       // Red — audio may cut out
    }
    if (quality !== this._connectionQuality) {
      const prevQuality = this._connectionQuality;
      this._connectionQuality = quality;

      // Propagate quality to all active audio players (adjusts jitter buffer)
      for (const player of this.audioPlayers.values()) {
        player.setConnectionQuality(quality);
      }

      // Adapt encoder bitrate to network conditions
      const newBitRate = this._bitrateByQuality[quality];
      if (newBitRate !== this._targetBitRate) {
        this._targetBitRate = newBitRate;
        this._applyAdaptiveBitrate(newBitRate);
        console.log(`[SimpleMumble] Quality ${prevQuality} → ${quality}: bitrate ${newBitRate/1000}kbps, RTT ${this._avgRtt.toFixed(0)}ms, jitter ${this._jitter.toFixed(0)}ms`);
      }

      if (this._qualityCallback) {
        this._qualityCallback(quality, this._avgRtt, this._jitter);
      }
    }
  }

  _applyAdaptiveBitrate(bitRate) {
    // opus-recorder's encoder worker accepts runtime bitrate changes
    // via the encoderBitRate option when reinitializing.
    // Since the recorder runs continuously, we store the target and apply
    // it on the next recorder restart (reconnect or manual restart).
    // For immediate effect, post a message to the encoder worker if available.
    if (this.recorder && this.recorder.encoder && typeof this.recorder.encoder.postMessage === 'function') {
      try {
        this.recorder.encoder.postMessage({ type: 'set_bitrate', value: bitRate });
      } catch (e) {
        // Worker may not support runtime bitrate changes — applied on next restart
      }
    }
  }

  async _initAudioPlayback() {
    try {
      console.log('[SimpleMumble] Initializing audio playback...');

      // Create audio context
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContextClass({
        sampleRate: 48000,
        latencyHint: 'interactive'
      });

      console.log('[SimpleMumble] AudioContext created - Initial state:', this.audioContext.state);

      // Try to resume (don't await — on iOS without user gesture this may never resolve)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().then(() => {
          console.log('[SimpleMumble] AudioContext resumed - New state:', this.audioContext.state);
        }).catch(() => {});
      }

      // Create master output gain
      this.masterOutputGain = this.audioContext.createGain();
      this.masterOutputGain.gain.value = 1.0;
      this.masterOutputGain.connect(this.audioContext.destination);

      // Prime the AudioContext with a silent buffer (critical for iOS)
      await this._primeAudioContext();

      // Start silent oscillator to keep iOS audio session alive
      this._startKeepAlive();

      console.log('[SimpleMumble] Audio playback initialized (48kHz) - Final state:', this.audioContext.state);
    } catch (err) {
      console.error('[SimpleMumble] Failed to initialize audio playback:', err);
      throw err;
    }
  }

  /**
   * Prime the AudioContext by playing a silent buffer
   * This "unlocks" audio playback on iOS Safari
   * CRITICAL: Play multiple buffers and wait for state to become "running"
   */
  async _primeAudioContext() {
    if (!this.audioContext) return;

    try {
      console.log('[SimpleMumble] Priming AudioContext - Current state:', this.audioContext.state);

      // Play 3 silent buffers to fully unlock iOS audio
      for (let i = 0; i < 3; i++) {
        const buffer = this.audioContext.createBuffer(1, 1, 48000);
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.start(0);

        // Small delay between buffers
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      // Try to reach "running" state, but don't block if it won't resume
      // (iOS without user gesture keeps it suspended — gesture handler will resume later)
      let attempts = 0;
      while (this.audioContext.state !== 'running' && attempts < 3) {
        this.audioContext.resume().catch(() => {});
        await new Promise(resolve => setTimeout(resolve, 50));
        attempts++;
      }

      console.log('[SimpleMumble] AudioContext primed - Final state:', this.audioContext.state);
    } catch (err) {
      console.warn('[SimpleMumble] Failed to prime AudioContext:', err);
    }
  }

  /**
   * Ensure AudioContext is running before playing audio
   * Call this before attempting playback, especially on iOS
   */
  async _ensureAudioContextRunning() {
    if (!this.audioContext) {
      console.error('[SimpleMumble] No AudioContext available');
      return false;
    }

    console.log('[SimpleMumble] Checking AudioContext state:', this.audioContext.state);

    // If not running, try to resume and prime
    if (this.audioContext.state !== 'running') {
      try {
        console.log('[SimpleMumble] AudioContext not running, attempting to resume...');
        await this.audioContext.resume();
        console.log('[SimpleMumble] AudioContext resume called - New state:', this.audioContext.state);

        // Prime again after resuming
        await this._primeAudioContext();
      } catch (err) {
        console.error('[SimpleMumble] Failed to resume AudioContext:', err);
        return false;
      }
    }

    const isRunning = this.audioContext.state === 'running';
    console.log('[SimpleMumble] AudioContext final state:', this.audioContext.state, '- Is running:', isRunning);
    return isRunning;
  }

  _startKeepAlive() {
    if (!this.audioContext || this.keepAliveOscillator) return;
    try {
      this.keepAliveOscillator = this.audioContext.createOscillator();
      this.keepAliveGain = this.audioContext.createGain();
      // Use 200 Hz (not 0!) - iOS Safari ignores DC/0Hz and suspends the audio session.
      // At gain 0.0001 this is completely inaudible but keeps the session alive.
      this.keepAliveOscillator.frequency.value = 200;
      this.keepAliveGain.gain.value = 0.0001;
      this.keepAliveOscillator.connect(this.keepAliveGain);
      this.keepAliveGain.connect(this.audioContext.destination);
      this.keepAliveOscillator.start();
      console.log('[SimpleMumble] Keep-alive oscillator started (200Hz, gain 0.0001)');
    } catch (e) {
      console.warn('[SimpleMumble] Failed to start keep-alive:', e);
    }
  }

  /**
   * Reset all audio players - discards any buffered/stale audio.
   * Call this when returning from background to avoid replaying old messages.
   */
  flushAudioPlayers() {
    // Discard all audio for 1 second - buffered WebSocket messages arrive AFTER this call
    this._discardAudioUntil = Date.now() + 1000;

    for (const player of this.audioPlayers.values()) {
      player.reset();
    }
    // Clear speaking state so stale indicators don't linger
    for (const user of this.users.values()) {
      clearTimeout(user.speakingTimeout);
      user.speaking = false;
    }
    if (this.onUsersChanged) {
      this.onUsersChanged(Array.from(this.users.values()));
    }
    console.log('[SimpleMumble] Flushed audio - discarding packets for 1s');
  }

  /**
   * Suspend audio playback (app going to background).
   * Voice packets are still parsed for speaking state/notifications,
   * but no audio is decoded or scheduled.
   */
  suspend() {
    this._suspended = true;
    console.log('[SimpleMumble] Suspended — audio playback paused');
  }

  /**
   * Resume audio playback (app returning to foreground).
   * Flushes any stale audio that was buffered while suspended.
   */
  resume() {
    this._suspended = false;
    this.flushAudioPlayers();
    console.log('[SimpleMumble] Resumed — stale audio flushed');
  }

  _stopKeepAlive() {
    if (this.keepAliveOscillator) {
      try {
        this.keepAliveOscillator.stop();
        this.keepAliveOscillator.disconnect();
        this.keepAliveGain.disconnect();
      } catch (e) { /* already stopped */ }
      this.keepAliveOscillator = null;
      this.keepAliveGain = null;
      console.log('[SimpleMumble] Keep-alive oscillator stopped');
    }
  }

  _parseChannelState(payload) {
    try {
      const channelInfo = {};
      let offset = 0;

      while (offset < payload.length) {
        if (offset >= payload.length) break;

        // Read field tag as varint (handles multi-byte tags for field numbers >= 16)
        const tagResult = this._decodeProtobufVarint(payload, offset);
        offset += tagResult.length;
        const fieldNum = tagResult.value >> 3;
        const wireType = tagResult.value & 0x7;

        if (fieldNum === 1 && wireType === 0) {
          const result = this._decodeProtobufVarint(payload, offset);
          channelInfo.channelId = result.value;
          offset += result.length;
        } else if (fieldNum === 2 && wireType === 0) {
          const result = this._decodeProtobufVarint(payload, offset);
          channelInfo.parent = result.value;
          offset += result.length;
        } else if (fieldNum === 3 && wireType === 2) {
          const result = this._decodeProtobufString(payload, offset);
          channelInfo.name = result.value;
          offset += result.length;
        } else if (fieldNum === 5 && wireType === 0) {
          const result = this._decodeProtobufVarint(payload, offset);
          channelInfo.position = result.value;
          offset += result.length;
        } else {
          if (wireType === 0) {
            const result = this._decodeProtobufVarint(payload, offset);
            offset += result.length;
          } else if (wireType === 2) {
            const lengthResult = this._decodeProtobufVarint(payload, offset);
            offset += lengthResult.length + lengthResult.value;
          } else if (wireType === 5) {
            offset += 4;
          } else if (wireType === 1) {
            offset += 8;
          } else {
            break;
          }
        }
      }

      if (channelInfo.channelId !== undefined) {
        const existing = this.channels.get(channelInfo.channelId);
        const updated = { ...existing, ...channelInfo };
        if (updated.position === undefined) updated.position = 0;
        this.channels.set(channelInfo.channelId, updated);
        console.log('[SimpleMumble] Channel:', updated.name || `ID ${updated.channelId}`);

        if (this.onChannelsChanged) {
          const sorted = Array.from(this.channels.values())
            .sort((a, b) => (a.position - b.position) || (a.name || '').localeCompare(b.name || ''));
          this.onChannelsChanged(sorted);
        }
      }
    } catch (e) {
      console.error('[SimpleMumble] Error parsing ChannelState:', e);
    }
  }

  switchChannel(channelId) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    console.log('[SimpleMumble] Switching to channel:', channelId);
    const sessionField = this._encodeProtobufVarint(1, this.sessionId);
    const channelField = this._encodeProtobufVarint(5, channelId);
    const payload = new Uint8Array(sessionField.length + channelField.length);
    payload.set(sessionField, 0);
    payload.set(channelField, sessionField.length);
    this._sendControlMessage(9, payload);
  }

  async _initRecorder() {
    console.log('[SimpleMumble] Initializing audio recorder...');

    try {
      // Get microphone permission ONCE at connect time
      // This prevents iOS from prompting on every PTT press
      console.log('[SimpleMumble] Requesting microphone access...');
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      console.log('[SimpleMumble] Microphone access granted');

      // Use a SEPARATE AudioContext for mic capture so the playback context
      // doesn't enter Chrome Android "communication" mode (earpiece routing).
      if (!this.micAudioContext || this.micAudioContext.state === 'closed') {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        this.micAudioContext = new AudioContextClass({ sampleRate: 48000 });
        console.log('[SimpleMumble] Mic AudioContext created - state:', this.micAudioContext.state);
      }

      this.recorder = new Recorder({
        encoderPath: './encoderWorker.min.js',
        encoderSampleRate: 48000,
        encoderApplication: 2048, // VOIP
        streamPages: true,
        maxFramesPerPage: 5, // 5 frames = 100ms per page (was 40 = 800ms latency)
        numberOfChannels: 1,
        encoderFrameSize: 20,
        encoderComplexity: 10,
        resampleQuality: 3,
        encoderBitRate: this._targetBitRate,
        // Opus FEC: adds ~20% overhead but allows receiver to recover lost packets
        // by decoding redundant data embedded in the NEXT packet
        encoderFEC: 1,
        // Tell Opus to expect 10% packet loss — tunes FEC redundancy accordingly.
        // Cellular and congested Wi-Fi commonly see 5-15% loss.
        encoderPacketLoss: 10,
        // Pass the pre-acquired mic stream so recorder doesn't call getUserMedia
        // Uses dedicated micAudioContext to avoid earpiece routing on Chrome Android
        sourceNode: this.micAudioContext.createMediaStreamSource(this.micStream)
      });

      // Handle encoded audio data - send in real-time
      this.recorder.ondataavailable = (typedArray) => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.recording === false) return;

        // Track when data last arrived (used by drain logic)
        this._lastOggPageTime = Date.now();

        // Parse Ogg pages to extract raw Opus frames
        const frames = this._parseOggPage(typedArray);

        if (frames.length === 0) return;

        // Count frames per state for diagnostics
        if (this.recording === true) this._framesSentActive += frames.length;
        else if (this.recording === 'draining') this._framesSentDrain += frames.length;

        // Send each frame immediately as a separate voice packet
        for (let i = 0; i < frames.length; i++) {
          const voicePacket = this._createVoicePacket(frames[i], true);
          this._sendControlMessage(1, voicePacket);
        }
      };

      // Handle recording stop (only on disconnect, not PTT release)
      this.recorder.onstop = () => {
        console.log('[SimpleMumble] Recorder stopped');
      };

      console.log('[SimpleMumble] Recorder initialized');

      // Start recorder immediately - it runs continuously.
      // PTT just gates which packets get sent (via this.recording flag).
      // This eliminates encoder spin-up delay on each PTT press.
      await this.recorder.start();
      console.log('[SimpleMumble] Recorder running (always-on, gated by PTT)');

      // CRITICAL: Re-resume AudioContexts after mic permission dialog
      // The permission dialog on iOS can suspend audio contexts
      console.log('[SimpleMumble] AudioContext state after mic permission:', this.audioContext.state);
      if (this.audioContext.state !== 'running') {
        console.log('[SimpleMumble] AudioContext suspended after mic dialog - resuming...');
        await this.audioContext.resume();
        await this._primeAudioContext();
        console.log('[SimpleMumble] AudioContext re-primed after mic dialog - state:', this.audioContext.state);
      }
      if (this.micAudioContext && this.micAudioContext.state !== 'running') {
        await this.micAudioContext.resume();
        console.log('[SimpleMumble] Mic AudioContext resumed after mic dialog - state:', this.micAudioContext.state);
      }
    } catch (error) {
      console.error('[SimpleMumble] Failed to init recorder:', error);
      throw error;
    }
  }

  startTransmission() {
    if (!this.connected) return;

    // If mic was deferred (e.g. iOS without user gesture), try acquiring now
    // PTT press IS a user gesture, so getUserMedia should work
    if (this._micDeferred && !this._micRetrying) {
      this._micRetrying = true;
      this._initRecorder().then(() => {
        this._micDeferred = false;
        this._micRetrying = false;
        console.log('[SimpleMumble] Mic acquired on PTT gesture');
        this.startTransmission(); // Retry now that mic is ready
      }).catch(err => {
        this._micRetrying = false;
        console.warn('[SimpleMumble] Mic still unavailable:', err.message);
      });
      return;
    }

    // Cancel any pending drain
    if (this._drainTimer) {
      clearTimeout(this._drainTimer);
      this._drainTimer = null;
    }

    // Already actively recording
    if (this.recording === true) return;

    // Resuming from drain — recorder still running, don't reset sequence
    if (this.recording === 'draining') {
      this.recording = true;
      return;
    }

    // Fresh start
    this.voiceSequence = 0;
    this.skippedHeaders = 0;
    this._framesSentActive = 0;
    this._framesSentDrain = 0;
    this.recording = true;

    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  stopTransmission(immediate = false) {
    if (!this.recording) return;

    // Clear any existing drain timer
    if (this._drainTimer) {
      clearTimeout(this._drainTimer);
      this._drainTimer = null;
    }

    if (immediate) {
      this.recording = false;
      this._sendTerminator();
      return;
    }

    // Enter draining state — ondataavailable still sends frames.
    // Short drain to flush in-flight encoder frames (1 page = 100ms).
    // Cap at 300ms since encoder is always-on and never stops producing.
    this.recording = 'draining';
    this._drainStart = Date.now();
    this._lastOggPageTime = Date.now();

    const checkDrain = () => {
      if (this.recording !== 'draining') return; // cancelled (re-press)
      const sinceLast = Date.now() - this._lastOggPageTime;
      const totalDrain = Date.now() - this._drainStart;
      if (sinceLast >= 150 || totalDrain >= 300) {
        this._drainTimer = null;
        this.recording = false;
        this._sendTerminator();
        console.log(`[SimpleMumble] === PTT SUMMARY === active:${this._framesSentActive} drain:${this._framesSentDrain} drainMs:${totalDrain} reason:${sinceLast >= 250 ? 'silence' : 'cap'}`);
        // Handle deferred device change (Bluetooth connected during transmission)
        if (this._deviceChangePending) {
          this._deviceChangePending = false;
          this._reacquireMic();
        }
      } else {
        this._drainTimer = setTimeout(checkDrain, 60);
      }
    };
    this._drainTimer = setTimeout(checkDrain, 60);
  }

  _sendTerminator() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.voiceSequence > 0) {
      const emptyFrame = new Uint8Array(1);
      const terminatorPacket = this._createVoicePacket(emptyFrame, true);
      this._sendControlMessage(1, terminatorPacket);
    }
  }

  _handleMessage(data) {
    if (data instanceof ArrayBuffer) {
      const bytes = new Uint8Array(data);

      // Check if this is a control message (first 2 bytes are message type)
      if (bytes.length >= 6) {
        const type = (bytes[0] << 8) | bytes[1];
        const length = (bytes[2] << 24) | (bytes[3] << 16) | (bytes[4] << 8) | bytes[5];

        // Skip logging for ping (Type 3) and voice (Type 1) messages to reduce console spam
        if (type !== 3 && type !== 1) {
          console.log('[SimpleMumble] Received control message - Type:', type, 'Length:', length, 'Total bytes:', bytes.length);
        }

        // Message types from Mumble.proto
        // 0 = Version
        // 2 = Authenticate
        // 5 = ServerSync (contains session ID)

        if (type === 0) {
          // Received Version from server
          console.log('[SimpleMumble] Received Version from server');
          // Send our Version and Authenticate
          this._sendVersion();
          this._sendAuthenticate();
        } else if (type === 1) {
          // UDPTunnel - voice packet from another user
          this._handleVoicePacket(bytes.slice(6)); // Skip 6-byte header
        } else if (type === 3) {
          // Received Ping response - record pong time
          this._lastPongTime = Date.now();
          // Calculate RTT only for the first response to OUR ping (not server-initiated pings)
          if (this.pingTimestamp) {
            const rtt = Date.now() - this.pingTimestamp;
            this.pingTimestamp = 0;  // Clear — only measure once per outgoing ping cycle
            this._rttHistory.push(rtt);
            if (this._rttHistory.length > 10) this._rttHistory.shift();
            this._updateQuality();
          }
          const pingPayload = bytes.slice(6);
          // Silently respond to pings (no logging to reduce console spam)
          this._sendControlMessage(3, pingPayload); // Type 3 = Ping
        } else if (type === 5) {
          // ServerSync - extract OUR session ID
          console.log('[SimpleMumble] Received ServerSync (Type 5)');

          const payload = bytes.slice(6);
          if (payload.length > 0) {
            let offset = 0;
            while (offset < payload.length) {
              const tagResult = this._decodeProtobufVarint(payload, offset);
              offset += tagResult.length;
              const fieldNum = tagResult.value >> 3;
              const wireType = tagResult.value & 0x7;

              // Field 1 is session (varint, wiretype 0)
              if (fieldNum === 1 && wireType === 0) {
                const result = this._decodeProtobufVarint(payload, offset);
                this.sessionId = result.value;
                offset += result.length;
                console.log('[SimpleMumble] Session ID:', this.sessionId);

                // Update isSelf flag for all users now that we know our session
                this.users.forEach((user, session) => {
                  user.isSelf = (session === this.sessionId);
                });
                if (this.onUsersChanged && this.users.size > 0) {
                  console.log('[SimpleMumble] Refreshing user list after ServerSync');
                  this.onUsersChanged(Array.from(this.users.values()));
                }

                if (this.onServerSync) this.onServerSync(this.sessionId);
                break;
              } else if (wireType === 0) {
                const result = this._decodeProtobufVarint(payload, offset);
                offset += result.length;
              } else if (wireType === 2) {
                const lengthResult = this._decodeProtobufVarint(payload, offset);
                offset += lengthResult.length + lengthResult.value;
              } else if (wireType === 5) {
                offset += 4;
              } else if (wireType === 1) {
                offset += 8;
              } else {
                break;
              }
            }
          }
        } else if (type === 7) {
          // ChannelState
          this._parseChannelState(bytes.slice(6));
        } else if (type === 8) {
          // ChannelRemove
          const removePayload = bytes.slice(6);
          if (removePayload.length > 0 && (removePayload[0] >> 3) === 1 && (removePayload[0] & 0x7) === 0) {
            const result = this._decodeProtobufVarint(removePayload, 1);
            this.channels.delete(result.value);
            if (this.onChannelsChanged) {
              const sorted = Array.from(this.channels.values())
                .sort((a, b) => (a.position - b.position) || (a.name || '').localeCompare(b.name || ''));
              this.onChannelsChanged(sorted);
            }
          }
        } else if (type === 12) {
          // UserRemove - user disconnected
          const removePayload12 = bytes.slice(6);
          if (removePayload12.length > 1 && (removePayload12[0] >> 3) === 1 && (removePayload12[0] & 0x7) === 0) {
            const result = this._decodeProtobufVarint(removePayload12, 1);
            const removedSession = result.value;
            // If the server removed OUR session, we were kicked (ghost disconnect)
            if (removedSession === this.sessionId) {
              console.warn('[SimpleMumble] Server removed our session - kicked (ghost/duplicate username)');
              this._kickedByServer = true;
            }
            const user = this.users.get(removedSession);
            if (user) {
              clearTimeout(user.speakingTimeout);
              this.users.delete(removedSession);
              const player = this.audioPlayers.get(removedSession);
              if (player) { player.destroy(); this.audioPlayers.delete(removedSession); }
              console.log('[SimpleMumble] User removed:', user.name || `Session ${removedSession}`);
              if (this.onUsersChanged) {
                this.onUsersChanged(Array.from(this.users.values()));
              }
            }
          }
        } else if (type === 9) {
          // UserState - other users in channel
          this._parseUserState(bytes.slice(6));
        } else if (type === 24) {
          // ServerConfig - just log it
          console.log('[SimpleMumble] Received ServerConfig (Type 24)');
        } else if (type === 26) {
          // Unknown type 26
          console.log('[SimpleMumble] Received Type 26');
        }
      } else if (bytes.length < 100) {
        console.log('[SimpleMumble] Received small message:', bytes.length, 'bytes -', Array.from(bytes.slice(0, 20)).map(b => b.toString(16).padStart(2, '0')).join(' '));
      }
    }
  }

  _encodeString(str) {
    const bytes = new TextEncoder().encode(str);
    return bytes;
  }

  _encodeProtobufString(fieldNum, str) {
    const strBytes = this._encodeString(str);
    const fieldTag = (fieldNum << 3) | 2; // Wire type 2 = length-delimited
    const lengthVarint = this._encodeProtobufVarintRaw(strBytes.length);

    const result = new Uint8Array(1 + lengthVarint.length + strBytes.length);
    let offset = 0;
    result[offset++] = fieldTag;
    result.set(lengthVarint, offset);
    offset += lengthVarint.length;
    result.set(strBytes, offset);

    return result;
  }

  _encodeProtobufVarint(fieldNum, value) {
    const fieldTag = (fieldNum << 3) | 0; // Wire type 0 = varint
    const valueVarint = this._encodeProtobufVarintRaw(value);

    const result = new Uint8Array(1 + valueVarint.length);
    result[0] = fieldTag;
    result.set(valueVarint, 1);

    return result;
  }

  _sendControlMessage(type, payload) {
    const header = new Uint8Array(6);
    // Type (2 bytes, big-endian)
    header[0] = (type >> 8) & 0xFF;
    header[1] = type & 0xFF;
    // Length (4 bytes, big-endian)
    header[2] = (payload.length >> 24) & 0xFF;
    header[3] = (payload.length >> 16) & 0xFF;
    header[4] = (payload.length >> 8) & 0xFF;
    header[5] = payload.length & 0xFF;

    const message = new Uint8Array(header.length + payload.length);
    message.set(header, 0);
    message.set(payload, header.length);

    // Log hex dump for Authenticate messages
    if (type === 2) {
      const hexDump = Array.from(payload).map(b => b.toString(16).padStart(2, '0')).join(' ');
      console.log('[SimpleMumble] Authenticate payload hex:', hexDump);
    }

    // Skip logging for voice (Type 1) and ping (Type 3) to avoid spam
    if (type !== 1 && type !== 3) {
      console.log('[SimpleMumble] Sending control message - Type:', type, 'Length:', payload.length);
    }
    this.ws.send(message.buffer);
  }

  _sendVersion() {
    // Version message (type 0)
    // Field 1: version_v1 (uint32) - e.g., 0x010205 for 1.2.5
    // Field 2: release (string) - e.g., "1.5.517"
    // Field 3: os (string)
    // Field 4: os_version (string)

    const version = this._encodeProtobufVarint(1, 0x010205); // Version 1.2.5
    const release = this._encodeProtobufString(2, '1.5.517');
    const os = this._encodeProtobufString(3, 'Web');
    const osVersion = this._encodeProtobufString(4, navigator.userAgent.substring(0, 50));

    const payload = new Uint8Array(
      version.length + release.length + os.length + osVersion.length
    );
    let offset = 0;
    payload.set(version, offset);
    offset += version.length;
    payload.set(release, offset);
    offset += release.length;
    payload.set(os, offset);
    offset += os.length;
    payload.set(osVersion, offset);

    this._sendControlMessage(0, payload);
  }

  _sendAuthenticate() {
    // Authenticate message (type 2)
    // Field 1: username (string) - required
    // Field 2: password (string) - optional
    // Field 3: tokens (repeated string) - optional
    // Field 4: celt_versions (repeated int32) - optional
    // Field 5: opus (bool) - set to true for Opus support

    // Use original username with minimal sanitization
    // Allow alphanumeric, dash, underscore
    let cleanUsername = (this.username || 'WebUser')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .substring(0, 30);

    if (!cleanUsername || cleanUsername.length < 2) {
      cleanUsername = 'WebUser';
    }

    console.log('[SimpleMumble] Authenticating as:', cleanUsername);

    const username = this._encodeProtobufString(1, cleanUsername); // FIELD 1!
    const password = this._encodeProtobufString(2, ''); // FIELD 2!
    const opus = new Uint8Array([0x28, 0x01]); // Field 5 (5<<3 | 0 = 40 = 0x28), value = 1 (true)

    const payload = new Uint8Array(username.length + password.length + opus.length);
    let offset = 0;
    payload.set(username, offset);
    offset += username.length;
    payload.set(password, offset);
    offset += password.length;
    payload.set(opus, offset);

    this._sendControlMessage(2, payload);
  }

  async disconnect() {
    console.log('[SimpleMumble] Disconnecting...');

    // Clear drain timer
    if (this._drainTimer) {
      clearTimeout(this._drainTimer);
      this._drainTimer = null;
    }

    if (this.recording) {
      this.stopTransmission(true);
    }

    this._stopPingInterval();

    // Stop and destroy all audio players
    for (const player of this.audioPlayers.values()) {
      player.destroy();
    }
    this.audioPlayers.clear();

    // Remove gesture handler
    if (this._userGestureHandler) {
      document.removeEventListener('touchstart', this._userGestureHandler);
      document.removeEventListener('click', this._userGestureHandler);
      this._userGestureHandler = null;
    }

    // Remove device change listener
    if (this._deviceChangeHandler) {
      navigator.mediaDevices.removeEventListener('devicechange', this._deviceChangeHandler);
      this._deviceChangeHandler = null;
    }

    // Stop always-on recorder
    if (this.recorder) {
      try { this.recorder.stop(); } catch (e) { /* already stopped */ }
    }

    // Stop keep-alive oscillator
    this._stopKeepAlive();

    // Release microphone stream
    if (this.micStream) {
      this.micStream.getTracks().forEach(track => track.stop());
      this.micStream = null;
      console.log('[SimpleMumble] Microphone stream released');
    }

    // Close mic audio context (separate from playback context)
    if (this.micAudioContext) {
      try { await this.micAudioContext.close(); } catch (e) { /* already closed */ }
      this.micAudioContext = null;
      console.log('[SimpleMumble] Mic audio context closed');
    }

    // Close audio context
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
      console.log('[SimpleMumble] Audio context closed');
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.connected = false;
    this.channels.clear();
    this.users.clear();
  }

  async reconnect(options = {}) {
    const { address, username, wsUrl: customWsUrl, onConnected, onError, onDisconnected,
            onUsersChanged, onChannelsChanged, onServerSync, onQualityChanged } = options;

    this.username = username || this.username;
    this.onUsersChanged = onUsersChanged;
    this.onChannelsChanged = onChannelsChanged;
    this.onDisconnected = onDisconnected;
    this.onServerSync = onServerSync;
    if (onQualityChanged) this._qualityCallback = onQualityChanged;

    // Reset quality tracking on reconnect
    this._rttHistory = [];
    this._connectionQuality = 'good';

    this._stopPingInterval();
    if (this.ws) { try { this.ws.close(); } catch(e) {} this.ws = null; }

    // Reset protocol state (NOT audio resources)
    this.connected = false;
    this.sessionId = 0;
    this.voiceSequence = 0;
    this.users.clear();
    this.channels.clear();

    // Create fresh WebSocket
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const port = window.location.protocol === 'https:' ? '8443' : '8080';
    const wsUrl = customWsUrl || `${protocol}//${address || window.location.hostname}:${port}/mumble-ws`;

    this.ws = new WebSocket(wsUrl, ['binary']);
    this.ws.binaryType = 'arraybuffer';

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('WebSocket timeout')), 10000);
      this.ws.onopen = () => { clearTimeout(timeout); this.connected = true; resolve(); };
      this.ws.onerror = (err) => { clearTimeout(timeout); reject(err); };
    });

    this.ws.onmessage = (event) => this._handleMessage(event.data);
    this.ws.onclose = (event) => {
      this.connected = false;
      this._stopPingInterval();
      if (this.onDisconnected) {
        this.onDisconnected({ code: event.code, reason: event.reason, wasClean: event.wasClean, kickedByServer: !!this._kickedByServer });
      }
      this._kickedByServer = false;
    };

    // Reuse AudioContext if alive, otherwise recreate
    if (!this.audioContext || this.audioContext.state === 'closed') {
      await this._initAudioPlayback();
    } else if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }

    // Resume mic AudioContext if suspended
    if (this.micAudioContext && this.micAudioContext.state === 'suspended') {
      this.micAudioContext.resume().catch(() => {});
    }

    // Reuse mic if alive, otherwise try re-acquire (may fail without gesture on iOS)
    if (!this.micStream || this.micStream.getTracks().every(t => t.readyState === 'ended')) {
      this._initRecorder().catch(err => {
        console.warn('[SimpleMumble] Mic re-acquire deferred:', err.message);
        this._micDeferred = true;
      });
    }

    this._startPingInterval();
    if (onConnected) onConnected();
  }

  async setAudioOutput(deviceId) {
    if (!this.audioContext) {
      console.warn('[SimpleMumble] Cannot set audio output - no audio context');
      return;
    }

    // Use setSinkId if supported (Chrome/Edge)
    if (typeof this.audioContext.setSinkId === 'function') {
      try {
        await this.audioContext.setSinkId(deviceId);
        console.log('[SimpleMumble] Audio output set to:', deviceId || 'default');
      } catch (err) {
        console.error('[SimpleMumble] Failed to set audio output:', err);
        throw err;
      }
    } else {
      console.log('[SimpleMumble] setSinkId not supported (Safari/iOS), using default output');
    }
  }

  /**
   * Handle audio device changes (Bluetooth connect/disconnect, headset plug).
   * Re-acquires mic stream so it routes to the new default input device.
   */
  async _handleDeviceChange() {
    // Don't re-acquire during active transmission — wait until PTT is released
    if (this.recording) {
      console.log('[SimpleMumble] Device change during transmission — deferring mic switch');
      this._deviceChangePending = true;
      return;
    }

    await this._reacquireMic();
  }

  async _reacquireMic() {
    if (!this.connected || this._micDeferred) return;

    console.log('[SimpleMumble] Re-acquiring mic for new audio device...');
    try {
      // Stop old recorder
      if (this.recorder) {
        try { this.recorder.stop(); } catch (e) { /* already stopped */ }
        this.recorder = null;
      }

      // Stop old mic stream
      if (this.micStream) {
        this.micStream.getTracks().forEach(track => track.stop());
        this.micStream = null;
      }

      // Close old mic AudioContext (new one created in _initRecorder)
      if (this.micAudioContext) {
        try { await this.micAudioContext.close(); } catch (e) { /* already closed */ }
        this.micAudioContext = null;
      }

      // Re-init with new default device
      await this._initRecorder();
      console.log('[SimpleMumble] Mic re-acquired for new audio device');

      // Notify app layer so it can refresh device dropdowns
      if (this.onDeviceChange) this.onDeviceChange();
    } catch (err) {
      console.warn('[SimpleMumble] Failed to re-acquire mic on device change:', err.message);
    }
  }
}
